import { STORAGE_KEY, ENGINE_URL_KEY } from './portalSessionKeys';

/**
 * MCP tools HTTP catalog base. In dev, CRA proxy (package.json) forwards relative /v1/* to MCP_HTTP_PORT.
 * For static builds (Cloudflare Pages, S3), set REACT_APP_MCP_API_BASE=https://your-mcp-host
 * so the dashboard, copy-paste MCP URLs, and catalog links target the MCP server (not only the engine).
 */
const MCP_API_BASE = (process.env.REACT_APP_MCP_API_BASE || '').replace(/\/$/, '');
const MCP_EDGE_BASE = (process.env.REACT_APP_MCP_EDGE_BASE || '').replace(/\/$/, '');
const BUILD_ENGINE_URL = (process.env.REACT_APP_ENGINE_URL || '').replace(/\/$/, '');

/**
 * Public MCP HTTP host (tool catalog + streamable /mcp). Prefer REACT_APP_MCP_API_BASE.
 * Do not fall back to the trading engine URL — the engine service does not expose /mcp or
 * /v1/tools/catalog in split deployments (Render mcp-server vs engine).
 */
function resolveMcpPublicBase() {
  if (MCP_API_BASE) {
    return MCP_API_BASE;
  }
  // Worker exposes catalog + /mcp with Bearer; do not use Pages origin (no MCP API there).
  if (MCP_EDGE_BASE) {
    return MCP_EDGE_BASE;
  }
  return '';
}

function readSavedSession() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function readSavedEngineUrl() {
  if (typeof window === 'undefined') return '';
  try {
    return (window.localStorage.getItem(ENGINE_URL_KEY) || '').trim().replace(/\/$/, '');
  } catch {
    return '';
  }
}

function resolveEngineBase() {
  // Prefer the host that issued the portal JWT — build-time URL can differ when
  // multiple Render engines exist (engine-bc2n vs ai-execution-engine).
  const session = readSavedSession();
  const fromSession = (session?.engineUrl || '').trim().replace(/\/$/, '');
  if (fromSession) return fromSession;
  if (BUILD_ENGINE_URL) return BUILD_ENGINE_URL;
  return readSavedEngineUrl();
}

function hasPortalJwtSession() {
  const session = readSavedSession();
  const token = (session?.token || '').trim();
  if (!token) return false;
  const mode = (session?.authMode || '').trim();
  // Google login historically stored authMode "password" while token is a JWT.
  if (mode === 'api_key') {
    return looksLikeJwt(token);
  }
  return mode === 'password' || mode === 'jwt' || mode === '' || looksLikeJwt(token);
}

function looksLikeJwt(token) {
  const parts = String(token || '').split('.');
  return parts.length === 3 && parts.every((p) => p.length > 0);
}

/**
 * Browser account-hub calls use the engine the user logged into (JWT always matches).
 * MCP catalog / streamable URLs still use REACT_APP_MCP_API_BASE.
 */
function resolveApiBaseForPath(path) {
  if (path.startsWith('/v1/account-hub/') && hasPortalJwtSession()) {
    const engine = resolveEngineBase();
    if (engine) return engine;
  }
  return resolveApiBase();
}

/** WebSocket URL for live market feed (never localhost in production builds). */
export function engineWsUrl() {
  const explicit = (process.env.REACT_APP_ENGINE_WS_URL || '').trim();
  if (explicit) return explicit;
  const base = resolveEngineBase();
  if (base) {
    return `${base.replace(/^http/i, 'ws')}/ws`;
  }
  if (process.env.NODE_ENV === 'development') {
    return 'ws://localhost:8080/ws';
  }
  return null;
}

/** ML action-log poll URL; omit in production when unset (avoids 127.0.0.1 noise). */
export function aiActionLogsUrl(limit = 30) {
  const base = (process.env.REACT_APP_AI_LOGS_URL || '').replace(/\/$/, '');
  if (base) {
    return `${base}/api/action-logs?limit=${limit}`;
  }
  if (process.env.NODE_ENV === 'development') {
    return `http://127.0.0.1:5050/api/action-logs?limit=${limit}`;
  }
  return null;
}

/**
 * Resolve the base URL for /v1/* calls. Priority:
 * 1. REACT_APP_MCP_API_BASE (explicit MCP catalog override)
 * 2. REACT_APP_ENGINE_URL (build-time backend)
 * 3. engineUrl stored by ApiKeyPortal (the URL the user just logged in to)
 * 4. relative origin (dev / same-origin deployments)
 */
function resolveApiBase() {
  if (MCP_API_BASE) return MCP_API_BASE;
  if (BUILD_ENGINE_URL) return BUILD_ENGINE_URL;
  const session = readSavedSession();
  const fromSession = (session?.engineUrl || '').trim().replace(/\/$/, '');
  if (fromSession) return fromSession;
  return readSavedEngineUrl();
}

function streamableMcpPath() {
  const pathRaw = (process.env.REACT_APP_MCP_STREAMABLE_PATH || '/mcp').trim() || '/mcp';
  return pathRaw.startsWith('/') ? pathRaw : `/${pathRaw}`;
}

/**
 * Public streamable MCP URL for clients that connect over HTTPS (e.g. ChatGPT custom MCP).
 * Uses REACT_APP_MCP_API_BASE + REACT_APP_MCP_STREAMABLE_PATH (/mcp by default), or current origin in dev.
 */
export function mcpStreamableConnectUrl() {
  const base = resolveMcpPublicBase();
  if (base) {
    return `${base}${streamableMcpPath()}`;
  }
  return null;
}

/** MCP URL via Cloudflare Worker (or other edge) when REACT_APP_MCP_EDGE_BASE is set. */
export function mcpEdgeStreamableUrl() {
  if (!MCP_EDGE_BASE) return null;
  return `${MCP_EDGE_BASE}${streamableMcpPath()}`;
}

/** Catalog URL on the MCP edge host (same Bearer as streamable). */
export function mcpEdgeCatalogHttpUrl(queryString = 'include_details=false&hierarchy=false') {
  if (!MCP_EDGE_BASE) return null;
  return `${MCP_EDGE_BASE}/v1/tools/catalog?${queryString}`;
}

export function engineApiUrl(path) {
  if (!path.startsWith('/')) {
    return path;
  }
  const base = resolveEngineBase();
  if (base) {
    return `${base}${path}`;
  }
  return path;
}

function authHeadersEngineSession(extra = {}) {
  const headers = { ...extra };
  const session = readSavedSession();
  const token = (session?.token || '').trim();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

/**
 * POST to the trading engine (not the MCP host). Requires a logged-in session JWT or same-origin engine.
 */
export async function enginePost(path, body) {
  const base = resolveEngineBase();
  if (!base) {
    throw new Error('Engine URL is not set. Log in via the portal or set REACT_APP_ENGINE_URL.');
  }
  const res = await fetch(engineApiUrl(path), {
    method: 'POST',
    headers: authHeadersEngineSession({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`POST ${path} failed (${res.status}): ${text.slice(0, 240)}`);
  }
  return readJsonBody(res);
}

/** Full HTTP URL for GET /v1/tools/catalog (for copy-paste in integration modals). */
export function mcpCatalogHttpUrl(queryString = 'include_details=false&hierarchy=false') {
  const base = resolveMcpPublicBase();
  if (!base) {
    return mcpApiUrl(`/v1/tools/catalog?${queryString}`);
  }
  return `${base}/v1/tools/catalog?${queryString}`;
}

export function mcpApiUrl(path) {
  if (!path.startsWith('/')) {
    return path;
  }
  const base = resolveApiBaseForPath(path);
  if (base) {
    return `${base}${path}`;
  }
  return path;
}

const API_KEY = process.env.REACT_APP_MCP_HTTP_API_KEY || '';

function authHeaders(extra = {}) {
  const headers = { ...extra };
  const session = readSavedSession();
  const token = (session?.token || '').trim();
  const authMode = (session?.authMode || '').trim();

  if (API_KEY) {
    headers['X-API-KEY'] = API_KEY;
  }

  if (!token) {
    return headers;
  }

  // Portal email/Google login stores a JWT; API-key login stores the raw key.
  if (authMode === 'password' || authMode === 'jwt') {
    headers.Authorization = `Bearer ${token}`;
  } else if (authMode === 'api_key' && !API_KEY) {
    headers['X-API-KEY'] = token;
  } else if (!headers.Authorization && !headers['X-API-KEY']) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

function authHeadersForPath(path, extra = {}) {
  if (path.startsWith('/v1/account-hub/') && hasPortalJwtSession() && resolveEngineBase()) {
    return authHeadersEngineSession(extra);
  }
  return authHeaders(extra);
}

async function mcpErrorMessage(method, path, res) {
  const text = await res.text();
  try {
    const body = JSON.parse(text);
    const msg = body.message || body.error || text;
    const reason = body.reason ? ` [${body.reason}]` : '';
    return `${method} ${path} failed (${res.status})${reason}: ${msg}`;
  } catch {
    return `${method} ${path} failed (${res.status})`;
  }
}

async function readJsonBody(res) {
  const text = await res.text();
  if (!text) {
    return null;
  }
  try {
    return JSON.parse(text);
  } catch {
    const hint = text.trimStart().startsWith('<')
      ? 'Got HTML instead of JSON. Make sure you are logged in (the engine URL is taken from your saved session), or set REACT_APP_MCP_API_BASE to your MCP/engine host.'
      : `Body: ${text.slice(0, 120)}`;
    throw new Error(hint);
  }
}

export async function mcpGet(path) {
  const res = await fetch(mcpApiUrl(path), {
    headers: authHeadersForPath(path),
  });
  if (!res.ok) {
    throw new Error(await mcpErrorMessage('GET', path, res));
  }
  return readJsonBody(res);
}

export async function mcpPost(path, body) {
  const res = await fetch(mcpApiUrl(path), {
    method: 'POST',
    headers: authHeadersForPath(path, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(await mcpErrorMessage('POST', path, res));
  }
  return readJsonBody(res);
}

export async function mcpDelete(path) {
  const res = await fetch(mcpApiUrl(path), {
    method: 'DELETE',
    headers: authHeadersForPath(path),
  });
  if (!res.ok) {
    throw new Error(await mcpErrorMessage('DELETE', path, res));
  }
  return readJsonBody(res);
}
